import React, { useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { authConfig } from '../../config/auth';
import { useAuth } from '../../contexts/AuthContext';
import { exportToCsv } from '../../utils/exportCsv';
import type { BackOfficeRewardsRow } from '@crewcast/shared';

function monthStart(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().slice(0, 10);
}

export default function BackOfficeRewardsPage() {
  const { token } = useAuth();
  const now = new Date();
  const [rows, setRows] = useState<BackOfficeRewardsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState(monthStart(now));
  const [dateTo, setDateTo] = useState(now.toISOString().slice(0, 10));
  const [companies, setCompanies] = useState<{ companyId: string; displayName: string }[]>([]);
  const [companyId, setCompanyId] = useState('');

  useEffect(() => {
    async function loadCompanies() {
      try {
        if (!token) return;
        const res = await fetch(`${authConfig.apiUrl}/api/backoffice/companies?pageSize=200`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) setCompanies(data.companies || []);
      } catch {
        // Non-fatal
      }
    }
    loadCompanies();
  }, []);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line

  async function fetchData() {
    setLoading(true);
    setError('');
    try {
      if (!token) throw new Error('Not authenticated');

      const params = new URLSearchParams({ dateFrom, dateTo });
      if (companyId) params.set('companyId', companyId);

      const res = await fetch(`${authConfig.apiUrl}/api/backoffice/rewards?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load rewards data');
      setRows(data.rows || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load rewards data');
    } finally {
      setLoading(false);
    }
  }

  function handleApply(e: React.FormEvent) {
    e.preventDefault();
    fetchData();
  }

  function handleExport() {
    exportToCsv(
      `crewcast-rewards-${dateFrom}-${dateTo}`,
      ['companyId', 'displayName', 'totalClaims', 'claimed', 'fulfilled', 'cancelled', 'totalPointsClaimed', 'totalPointsFulfilled'],
      rows as unknown as Record<string, unknown>[]
    );
  }

  const totalClaims = rows.reduce((s, r) => s + r.totalClaims, 0);
  const totalClaimed = rows.reduce((s, r) => s + r.claimed, 0);
  const totalFulfilled = rows.reduce((s, r) => s + r.fulfilled, 0);
  const totalPoints = rows.reduce((s, r) => s + r.totalPointsClaimed, 0);

  return (
    <>
      <div className="bo-page-header">
        <h1 className="bo-page-title">Rewards & Fulfillment</h1>
        <p className="bo-page-subtitle">Reward claims lifecycle by account</p>
      </div>

      {/* Summary */}
      <div className="bo-summary-row">
        <div className="bo-summary-item">
          <div className="bo-summary-item-label">Total Claims</div>
          <div className="bo-summary-item-value">{loading ? '—' : totalClaims}</div>
        </div>
        <div className="bo-summary-item">
          <div className="bo-summary-item-label">Pending Fulfillment</div>
          <div className="bo-summary-item-value" style={{ color: 'var(--color-warning)' }}>{loading ? '—' : totalClaimed}</div>
        </div>
        <div className="bo-summary-item">
          <div className="bo-summary-item-label">Fulfilled</div>
          <div className="bo-summary-item-value" style={{ color: 'var(--color-success)' }}>{loading ? '—' : totalFulfilled}</div>
        </div>
        <div className="bo-summary-item">
          <div className="bo-summary-item-label">Total Points Claimed</div>
          <div className="bo-summary-item-value">{loading ? '—' : totalPoints.toLocaleString()}</div>
        </div>
      </div>

      {/* Filters */}
      <form className="bo-filters" onSubmit={handleApply}>
        <div className="bo-filter-group">
          <label className="bo-filter-label">From</label>
          <input
            className="bo-filter-input"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="bo-filter-group">
          <label className="bo-filter-label">To</label>
          <input
            className="bo-filter-input"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div className="bo-filter-group">
          <label className="bo-filter-label">Account</label>
          <select
            className="bo-filter-select crewcast-select"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
          >
            <option value="">All accounts</option>
            {companies.map((c) => (
              <option key={c.companyId} value={c.companyId}>{c.displayName}</option>
            ))}
          </select>
        </div>

        <div className="bo-filter-actions">
          <button type="submit" className="bo-btn bo-btn-primary" disabled={loading}>
            Apply
          </button>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={fetchData} disabled={loading}>
            <RefreshCw size={14} />
          </button>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={handleExport} disabled={loading || rows.length === 0}>
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="bo-table-card">
        <div className="bo-table-header">
          <span className="bo-table-title">
            Reward Claims by Account
            <span className="bo-table-count">({rows.length})</span>
          </span>
        </div>

        {loading ? (
          <div className="bo-loading">Loading...</div>
        ) : error ? (
          <div className="bo-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="bo-empty">No reward claims for the selected range.</div>
        ) : (
          <div className="bo-table-wrapper">
            <table className="bo-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th className="num">Total Claims</th>
                  <th className="num">Pending</th>
                  <th className="num">Fulfilled</th>
                  <th className="num">Cancelled</th>
                  <th className="num">Points Claimed</th>
                  <th className="num">Points Fulfilled</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.companyId}>
                    <td style={{ fontWeight: 'var(--font-weight-semibold)' as any }}>{r.displayName}</td>
                    <td className="num">{r.totalClaims}</td>
                    <td className="num">
                      {r.claimed > 0 ? (
                        <span className="bo-badge bo-badge-yellow">{r.claimed}</span>
                      ) : (
                        <span>0</span>
                      )}
                    </td>
                    <td className="num">
                      {r.fulfilled > 0 ? (
                        <span className="bo-badge bo-badge-green">{r.fulfilled}</span>
                      ) : (
                        <span>0</span>
                      )}
                    </td>
                    <td className="num">{r.cancelled}</td>
                    <td className="num">{r.totalPointsClaimed.toLocaleString()}</td>
                    <td className="num">{r.totalPointsFulfilled.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
